import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersService, CreateUserDto } from '../../core/services/users.service';
import { User } from '../../core/models/user.model';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    TranslateModule
  ],
  styles: [`
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
    .page-title { margin: 0; font-size: 28px; font-weight: 700; color: #1F2937; }
    .create-button { height: 42px; border-radius: 4px; font-weight: 600; }
    .table-container { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; overflow: hidden; }
    table { width: 100%; background: #fff; border-radius: 0; }
    th.mat-mdc-header-cell { background: #fff; color: #4B5563; font-weight: 600; border-bottom: 1px solid #D1D5DB; }
    td.mat-mdc-cell { color: #374151; border-bottom: 1px solid #E5E7EB; }
    .actions-header { text-align: right !important; }
    .actions-cell { text-align: right !important; white-space: nowrap; width: 1%; }
  `],
  template: `
    <div class="page-header">
      <h1 class="page-title">{{ 'users.title' | translate }}</h1>
      <button mat-raised-button color="primary" class="create-button" (click)="openCreate()">
        <mat-icon>add</mat-icon>
        {{ 'users.create' | translate }}
      </button>
    </div>
    <div class="table-container" *ngIf="data.length">
      <table mat-table [dataSource]="data" class="users-table">
      <ng-container matColumnDef="email">
        <th mat-header-cell *matHeaderCellDef>{{ 'users.email' | translate }}</th>
        <td mat-cell *matCellDef="let u">{{ u.email }}</td>
      </ng-container>
      <ng-container matColumnDef="fullName">
        <th mat-header-cell *matHeaderCellDef>{{ 'users.fullName' | translate }}</th>
        <td mat-cell *matCellDef="let u">{{ u.fullName }}</td>
      </ng-container>
      <ng-container matColumnDef="roles">
        <th mat-header-cell *matHeaderCellDef>{{ 'users.roles' | translate }}</th>
        <td mat-cell *matCellDef="let u">{{ u.roles?.join(', ') }}</td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="table-actions">{{ 'common.actions' | translate }}</th>
        <td mat-cell *matCellDef="let u" class="table-actions">
          <button mat-icon-button class="action-button delete-button" (click)="confirmDelete(u)"><mat-icon svgIcon="delete-custom"></mat-icon></button>
        </td>
      </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;"></tr>
      </table>
    </div>
    <div *ngIf="!data.length">{{ 'users.noData' | translate }}</div>
  `
})
export class UsersComponent implements OnInit {
  constructor(
    private users: UsersService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private t: TranslateService
  ) {}

  cols = ['email', 'fullName', 'roles', 'actions'];
  data: User[] = [];

  ngOnInit(): void { this.reload(); }

  reload() { this.users.getAll().subscribe(list => (this.data = list)); }

  openCreate() {
    const ref = this.dialog.open(UserDialogComponent, { data: null });
    ref.afterClosed().subscribe((dto?: CreateUserDto) => {
      if (!dto) return;
      this.users.create(dto).subscribe({
        next: () => { this.snack.open(this.t.instant('users.created'), this.t.instant('common.close'), { duration: 2000 }); this.reload(); },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  confirmDelete(user: User) {
    const yes = confirm(this.t.instant('users.confirmDelete', { email: user.email }));
    if (!yes) return;
    this.users.delete(user.id).subscribe({
      next: () => { this.snack.open(this.t.instant('users.deleted'), this.t.instant('common.close'), { duration: 2000 }); this.reload(); },
      error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.deleteError'), this.t.instant('common.close'), { duration: 3000 })
    });
  }
}

@Component({
  selector: 'app-user-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule
  ],
  template: `
    <h2 mat-dialog-title>{{ 'users.create' | translate }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()" style="padding: 0 24px 16px 24px; display:block; width:420px; max-width:100%;">
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'users.email' | translate }}</mat-label>
        <input matInput formControlName="email" type="email" required>
      </mat-form-field>
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'users.fullName' | translate }}</mat-label>
        <input matInput formControlName="fullName">
      </mat-form-field>
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'users.password' | translate }}</mat-label>
        <input matInput formControlName="password" type="password" required>
      </mat-form-field>
      <mat-form-field style="width:100%" appearance="outline">
        <mat-label>{{ 'users.role' | translate }}</mat-label>
        <mat-select formControlName="role" required>
          <mat-option value="user">User</mat-option>
          <mat-option value="admin">Admin</mat-option>
        </mat-select>
      </mat-form-field>
      <div style="margin-top:12px; display:flex; gap:8px; justify-content:flex-end;">
        <button mat-stroked-button mat-dialog-close>{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">{{ 'common.save' | translate }}</button>
      </div>
    </form>
  `
})
export class UserDialogComponent {
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: null
  ) {}

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    fullName: [''],
    password: ['', [Validators.required, Validators.minLength(6)]],
    role: ['user', [Validators.required]]
  });

  save() {
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.value as CreateUserDto);
  }
}
